import { useCallback } from 'react';

import { useIndirectTaxRegionJurisdictions } from '@commenda/api/indirect-tax/registrations/useIndirectTaxRegionJurisdictions';
import { useSupportedRegions } from '@commenda/api/indirect-tax/registrations/useSupportedRegions';

import { IndirectTaxRegionHelper } from '../helpers/IndirectTaxRegionHelper';
import { IndirectTaxRegion } from '../types';

interface UseIndirectTaxRegionScopeParams {
  corporationId: number;
  region: IndirectTaxRegion;
}

export function useIndirectTaxRegionScope({
  corporationId,
  region,
}: UseIndirectTaxRegionScopeParams) {
  const isAllRegions = region === IndirectTaxRegion.ALL_REGIONS;
  const {
    data: supportedRegions,
    isLoading: isLoadingRegions,
    error: regionsError,
    mutate: retryRegions,
  } = useSupportedRegions();
  const supportedRegion =
    supportedRegions.find(
      (supported) =>
        supported.regionId ===
        IndirectTaxRegionHelper.getSupportedRegionId(region)
    ) ?? null;
  const {
    jurisdictions,
    jurisdictionIds,
    isLoading: isLoadingJurisdictions,
    error: jurisdictionsError,
    retry: retryJurisdictions,
  } = useIndirectTaxRegionJurisdictions({
    corporationId,
    region: supportedRegion,
    enabled: !isAllRegions && Boolean(corporationId),
  });
  const isLoading =
    !isAllRegions && (isLoadingRegions || isLoadingJurisdictions);
  const error = isAllRegions ? null : (regionsError ?? jurisdictionsError);
  const isUnavailable =
    Boolean(corporationId) &&
    !isAllRegions &&
    !isLoadingRegions &&
    !regionsError &&
    supportedRegion === null;
  const isResolved =
    Boolean(corporationId) &&
    (isAllRegions || (!isLoading && !error && supportedRegion !== null));
  const retry = useCallback(async () => {
    if (isAllRegions) return;
    await retryRegions();
    await retryJurisdictions();
  }, [isAllRegions, retryRegions, retryJurisdictions]);

  return {
    jurisdictions,
    jurisdictionIds,
    isResolved,
    isUnavailable,
    isLoading,
    error,
    retry,
  };
}
